import { TYPES } from '@/app/components/atomic/AppIcon'
import dynamic from 'next/dynamic'
import React, { useMemo } from 'react'

const AppIcon = dynamic(() => import('@/app/components/atomic/AppIcon'))

const Tooltip = dynamic(() => import('react-tooltip').then((mod) => mod.Tooltip), { ssr: false })

interface AppTooltipWrapperProps {
    id: string | number
    title?: string
    text: string
    children: React.ReactNode
    iconClassName?: string
    iconType?: keyof typeof TYPES
    className?: string
    style?: React.CSSProperties
    theme?: 'light' | 'dark' | 'warning' | 'error' | 'success' | 'info' | 'loan'
}

export default function AppTooltipWrapper({
    id,
    children,
    title,
    text,
    iconClassName,
    iconType = 'solid',
    theme = 'dark',
    className = 'flex items-center justify-start gap-2',
    style,
}: AppTooltipWrapperProps) {
    const isLoanTheme = theme === 'loan'

    // v6 takes a ReactNode: markup comes from the code or back-office config, never from end users
    const tooltipContent = useMemo(() => {
        const body = <span dangerouslySetInnerHTML={{ __html: text }} />

        if (isLoanTheme && title) {
            return (
                <div>
                    <div className="rounded-t-[5px] bg-[#F6EDEA] px-[9px] py-[5px] text-[1.1em] font-bold text-black">
                        {title}
                    </div>
                    <div className="p-[9px] text-[0.8125rem] text-[#333333]">{body}</div>
                </div>
            )
        }
        if (isLoanTheme) {
            return <div className="p-[9px] text-[0.8125rem] text-[#333333]">{body}</div>
        }
        if (title) {
            return (
                <div>
                    <strong className="mb-1 block">{title}</strong>
                    {body}
                </div>
            )
        }
        return body
    }, [title, text, isLoanTheme])

    return (
        <>
            <div className={className} style={style} {...(!iconClassName ? { 'data-tooltip-id': String(id) } : {})}>
                {children}
                {iconClassName && (
                    <span data-tooltip-id={String(id)}>
                        <AppIcon iconClassName={iconClassName} className={`cursor-help ${isLoanTheme ? 'text-[#4A5565] !text-[20px] !leading-[20px]' : 'text-blue-700'}`} type={iconType} />
                    </span>
                )}
            </div>

            <Tooltip
                id={String(id)}
                content={tooltipContent}
                variant={isLoanTheme ? 'light' : theme}
                opacity={1}
                className={`z-50 max-w-[20rem] font-normal shadow-[0_5px_30px_rgba(0,0,0,0.1)] rounded-[5px] ${
                    isLoanTheme
                        ? '!p-0 !bg-white border border-[#F6EDEA]'
                        : theme === 'light'
                        ? 'p-2 text-gray-800'
                        : 'p-2 text-gray-200'
                }`}
                delayShow={50}
            />
        </>
    )
}
